/**
 * ModalityPicker (SK-CAPTURE-2, T-21 / §7.M3) — long-press alt-ingress picker
 * (Upload / Video / Text, PWA-SCREEN-SKILL-MAP §1). Reconciled to Minimal Dark
 * Mode: the three options render as 90px amber circles arced ABOVE the record
 * button (inline arc positions, matching `Mark PWA Preview.dc.html`), and the
 * whole surface is a transparent full-inset scrim whose background tap closes
 * the picker (tap-away). There is no explicit Cancel button — dismissal is the
 * scrim tap, per the preview.
 *
 * This component's own responsibility stays narrow: it surfaces the three
 * options and reports the selection via `onSelect`. The capture behavior each
 * option drives (part role/mime, text payload) is the host's integration
 * decision (app.tsx `handleSelectModality`), not this component's.
 */

export type ModalityOption = "upload" | "video" | "text";

export interface ModalityPickerProps {
  open: boolean;
  onSelect: (modality: ModalityOption) => void;
  onClose: () => void;
}

/** Fan-arc placement above the record button — copied from the preview's
 * inline geometry (`left:50%` + per-option `bottom`/`translateX`). The skin
 * (SK-CAPTURE-2) does not position the individual circles, so the geometry
 * lives here. */
const ARC: Record<ModalityOption, { bottom: string; tx: string }> = {
  upload: { bottom: "322px", tx: "-174px" },
  video: { bottom: "376px", tx: "-45px" },
  text: { bottom: "322px", tx: "84px" },
};

function optionStyle(pos: { bottom: string; tx: string }) {
  return {
    position: "absolute",
    left: "50%",
    bottom: pos.bottom,
    transform: `translateX(${pos.tx})`,
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    border: "none",
    background: "var(--mark-accent-amber)",
    color: "var(--mark-ink-on-amber)",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 700,
    boxShadow: "var(--mark-shadow-popover)",
    cursor: "pointer",
  } as const;
}

export function ModalityPicker({ open, onSelect, onClose }: ModalityPickerProps) {
  if (!open) return null;

  // Stop the option tap from bubbling to the scrim's tap-away `onClose`, so a
  // selection selects without also firing a close race.
  const pick = (modality: ModalityOption) => (e: Event) => {
    e.stopPropagation();
    onSelect(modality);
  };

  return (
    <div
      data-skin-slot="SK-CAPTURE-2"
      data-testid="modality-picker"
      role="menu"
      onClick={onClose}
      style={{ position: "absolute", inset: 0, zIndex: 10, background: "transparent" }}
    >
      <button type="button" role="menuitem" data-testid="modality-upload" onClick={pick("upload")} style={optionStyle(ARC.upload)}>
        Upload
      </button>
      <button type="button" role="menuitem" data-testid="modality-video" onClick={pick("video")} style={optionStyle(ARC.video)}>
        Video
      </button>
      <button type="button" role="menuitem" data-testid="modality-text" onClick={pick("text")} style={optionStyle(ARC.text)}>
        Text
      </button>
    </div>
  );
}
